import { useEffect, useMemo, useState } from 'react'
import { Plus, RefreshCw } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ActiveFilters, FilterBar, SearchInput } from '@/shared/ui/filters'
import { CursorPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useCan } from '@/shared/auth/session'
import { fmtDate, fmtNumber } from '@/shared/lib/format'
import type { ProgramResponse } from '@/shared/api/types'
import { listPrograms } from './api'
import { ProgramDrawer } from './ProgramDrawer'
import { ProgramFormDialog } from './ProgramFormDialog'
import { Money, PlannedChip } from './parts'
import {
  CATEGORY_ORDER, CategoryBadge, ChannelBadge, PROGRAM_STATUS_ORDER,
  ProgramStatusBadge, categoryLabel, programStatusLabel,
} from './vocab'

/**
 * The programme list.
 *
 * ## `q` never carries an identifier
 *
 * A digits-only 7–13 character query is refused CLIENT-SIDE before the request
 * is made, and the search box says why. The server refuses it too, with
 * `program/identifier-in-query` — but by then the value is already in the
 * browser's history and, on a real deployment, in an access log.
 *
 * Seven digits is the floor because a Kenyan national ID is 7–8 of them. The
 * mobilizer roster's equivalent guard starts at nine because its lookup takes a
 * phone only; copying that range here would let every national ID through.
 *
 * ## `spentAmount` is derived and there is no input for it
 *
 * It is a trigger-maintained rollup of the disbursement ledger. The column is
 * read-only everywhere in this feature, and the create/edit form has no field
 * for it — PollSphere carried five money columns that no code kept in agreement
 * and showed a different total on three screens.
 */
export function ProgramsListPage() {
  const canRead = useCan('program:read')
  const canCreate = useCan('program:create')

  const [params, setParams] = useSearchParams()
  const [draft, setDraft] = useState(params.get('q') ?? '')
  const debounced = useDebounced(draft, 300)

  // An identifier in a query string lands in access logs, browser history and
  // Referer headers. Refuse it here, not after a round trip.
  //
  // The separators stripped and the leading `+` allowed both matter: a phone
  // number typed the way people actually write one — `+254 712 345 678` — is a
  // national-scale PII leak that the first version of this guard let straight
  // through, because `+` was neither stripped nor permitted, so the pattern
  // failed and the value went to `?q=`.
  //
  // The guard runs on `debounced`, NOT on `draft`, because `debounced` is what
  // gets sent. Testing the live text opened a 300ms window in which the two
  // disagreed: type an identifier, pause, then type one more character, and for
  // one render the guard saw the new text (not an identifier) while the request
  // still carried the old (an identifier). One keystroke was enough to put it in
  // the address bar.
  const digits = debounced.replace(/[\s()-]/g, '')
  const looksLikeIdentifier = /^\+?\d{7,13}$/.test(digits)
  const q = looksLikeIdentifier ? '' : debounced

  const status = params.get('status') ?? ''
  const category = params.get('category') ?? ''

  useEffect(() => {
    if (looksLikeIdentifier) return
    const sp = new URLSearchParams(params)
    if (debounced) sp.set('q', debounced); else sp.delete('q')
    if (sp.toString() !== params.toString()) setParams(sp, { replace: true })
    // `params` is intentionally not a dep: including it re-runs on every render
    // that touches the query string and fights the user's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, looksLikeIdentifier])

  // The WIRE names, which the controller spells `status` and `category`. Sending
  // `statusCode`/`categoryCode` is not rejected — Spring binds nothing and the
  // list comes back unfiltered under a dropdown that says otherwise.
  const filters = useMemo(() => ({
    q: q || undefined,
    status: status || undefined,
    category: category || undefined,
  }), [q, status, category])

  const page = useCursorPage<ProgramResponse>(
    (cursor) => listPrograms(filters, cursor),
    [JSON.stringify(filters)],
  )

  const [openId, setOpenId] = useState<string | null>(params.get('open'))
  const [creating, setCreating] = useState(false)

  const setFilter = (key: string, value: string) => {
    const sp = new URLSearchParams(params)
    if (value) sp.set(key, value); else sp.delete(key)
    setParams(sp, { replace: true })
  }

  if (!canRead) return <Forbidden what="programmes" />

  const rows = page.rows
  // Computed over ONE PAGE, so they are labelled as such. A card headed
  // "Programmes 50" that changes when you page is a portfolio total that is not
  // one; the portfolio figures live on the analytics overview, which asks the
  // server for them.
  const active = rows.filter((r) => r.statusCode === 'ACTIVE').length
  const draftCount = rows.filter((r) => r.statusCode === 'DRAFT').length
  const withoutTargeting = rows.filter((r) => !r.activeRuleVersion).length
  const pageScope = page.page > 0 || page.hasMore ? 'on this page' : undefined

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Programmes"
        description="What the campaign delivers to people — and to whom, at what cost, with what effect."
        right={
          <div className="flex items-center gap-2">
            <button type="button" className="erp-btn" aria-label="Refresh" onClick={page.reload}>
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            {canCreate && (
              <button type="button" className="erp-btn-primary" onClick={() => setCreating(true)}>
                <Plus className="h-3.5 w-3.5" /> New programme
              </button>
            )}
          </div>
        }
      />

      <KpiStrip>
        <MetricCard label={pageScope ? 'Programmes on this page' : 'Programmes'}
          value={fmtNumber(rows.length)}
          sub={pageScope ? <Link to="/programs/analytics" className="underline underline-offset-2">portfolio totals</Link> : undefined} />
        <MetricCard label="Active" value={fmtNumber(active)} sub={pageScope} />
        <MetricCard label="Draft" value={fmtNumber(draftCount)} sub={pageScope} />
        {/* A programme with no published rule version has an UNDEFINED eligible
            pool, so every coverage number for it would divide by an unknown.
            Surfacing the count here is cheaper than explaining it later. */}
        <MetricCard
          label="No targeting yet"
          value={fmtNumber(withoutTargeting)}
          sub={withoutTargeting
            ? `Cannot be activated — no eligible pool${pageScope ? ' · on this page' : ''}`
            : `Every programme ${pageScope ? 'on this page ' : ''}has a published rule`}
        />
      </KpiStrip>

      <FilterBar>
        <SearchInput value={draft} onChange={setDraft} placeholder="Search by name or code" />
        <select className="erp-input" aria-label="Status" value={status}
          onChange={(e) => setFilter('status', e.target.value)}>
          <option value="">All statuses</option>
          {PROGRAM_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>{programStatusLabel(s)}</option>
          ))}
        </select>
        <select className="erp-input" aria-label="Category" value={category}
          onChange={(e) => setFilter('category', e.target.value)}>
          <option value="">All categories</option>
          {CATEGORY_ORDER.map((c) => (
            <option key={c} value={c}>{categoryLabel(c)}</option>
          ))}
        </select>
      </FilterBar>

      {looksLikeIdentifier && (
        <ErrorStrip message="That looks like a national ID or a phone number. Identifiers must not appear in a URL — open a programme, go to its Beneficiaries tab and use the lookup there, which sends the value in the request body." />
      )}

      <ActiveFilters
        count={(status ? 1 : 0) + (category ? 1 : 0) + (q ? 1 : 0)}
        onClear={() => setParams(new URLSearchParams(), { replace: true })}
      />

      <Panel>
        <ErrorStrip message={page.error} />
        <ErpTable head={(
          <>
            <Th>Programme</Th>
            <Th>Category</Th>
            <Th>Status</Th>
            <Th>Channel</Th>
            <Th>Runs</Th>
            <Th className="text-right">Budget</Th>
            <Th className="text-right">Spent</Th>
            <Th>Targeting</Th>
          </>
        )}>
          {page.loading && rows.length === 0 ? (
            <LoadingRows colSpan={8} />
          ) : rows.length === 0 ? (
            <EmptyRow colSpan={8} message="No programmes match these filters." />
          ) : rows.map((r) => (
            <tr
              key={r.id}
              className="erp-row cursor-pointer"
              onClick={() => setOpenId(r.id)}
              // A row that opens a drawer IS a button, so it announces as one
              // and answers to both keys that activate a button. Enter alone
              // leaves Space doing nothing, which is the half-implemented
              // version screen-reader users have learned to distrust.
              // NOT `role="button"`: that overrides the implicit `row`, orphans
              // every `<td>` from its required row parent, and makes the label the
              // element's entire accessible name — so a screen reader announces
              // "Open Kisumu Bursary, button" and the eight cells and their column
              // headers vanish. The tab stop and the key handler give keyboard
              // access without taking the table apart.
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenId(r.id) }
              }}
            >
              <Td>
                <div className="font-medium">{r.name}</div>
                <div className="text-erp-xs text-muted-foreground">
                  {r.code}
                  {/* NULL scope means NATIONAL, and a national programme is
                      visible to everybody — saying so beats an empty cell. */}
                  {r.scopeUnitPath === null && <span className="ml-1.5">· National</span>}
                </div>
              </Td>
              <Td><CategoryBadge code={r.categoryCode} /></Td>
              <Td><ProgramStatusBadge code={r.statusCode} /></Td>
              <Td><ChannelBadge code={r.deliveryChannelCode} /></Td>
              <Td className="text-erp-xs text-muted-foreground whitespace-nowrap">
                {fmtDate(r.startsOn)}{r.endsOn ? ` → ${fmtDate(r.endsOn)}` : ''}
              </Td>
              <Td className="text-right">
                <Money value={r.budgetAmount} currency={r.currency} />
              </Td>
              <Td className="text-right">
                <Money value={r.spentAmount} currency={r.currency} />
              </Td>
              <Td>
                {r.activeRuleVersion
                  ? <span className="text-erp-xs">v{r.activeRuleVersion}</span>
                  : <span className="text-erp-xs text-muted-foreground">none</span>}
                <PlannedChip value={r.expectedUpliftPp} />
              </Td>
            </tr>
          ))}
        </ErpTable>
        <CursorPager
          page={page.page}
          count={rows.length}
          hasMore={page.hasMore}
          canGoBack={page.canGoBack}
          onBack={page.back}
          onNext={page.next}
          loading={page.loading}
          unit="programmes"
        />
      </Panel>

      {openId && (
        <ProgramDrawer
          // Keyed to the subject: without this, opening B while A is mounted
          // reuses A's tab state and its in-flight requests, and the drawer can
          // show one programme's milestones under another's name.
          key={openId}
          programId={openId}
          onClose={() => setOpenId(null)}
          onChanged={page.reload}
        />
      )}

      {creating && (
        <ProgramFormDialog
          onClose={() => setCreating(false)}
          onSaved={(p: ProgramResponse) => { setCreating(false); page.reload(); setOpenId(p.id) }}
        />
      )}
    </div>
  )
}
